import property from 'lodash/property';

import { COUPON_QUERY_PARAM } from '@exp/components/products/constants';
import { TIME_DURATIONS } from '@exp/components/time';

import {
  GUIDELINES_PARTS,
  INTEGER_RADIX,
  NG_STORAGE_GUIDELINES,
  PRIZE_QUERY_PARAM,
  PRIZE_STATUS,
  type PrizeStatus,
} from './constants';
import type ProductTypes from '../../product/productTypes';
import type SingleCheckoutTypes from '../../singleCheckout/singleCheckoutTypes';

/**
 * @import * as CheckoutPropTypes from '../../checkout/checkoutPropTypes';
 * @import * as ProductPropTypes from '../../product/productPropTypes';
 */

export const createCheckoutUrl = (
  groupKey: string,
  couponId: string | number = null,
  prizeId: string | number = null,
  native = false,
): string => {
  const params = [
    couponId && `${COUPON_QUERY_PARAM}=${couponId}`,
    prizeId && `${PRIZE_QUERY_PARAM}=${prizeId}`,
  ].filter(Boolean);
  const query = params.length ? `?${params.join('&')}` : '';
  return `${native ? document.location.origin : ''}/checkout/${groupKey}${query}`;
};

/**
 * Maps prizes of a given status into coupon-shaped facades so they can be rendered
 * and selected alongside real coupons in {@link CartCouponList}/{@link Coupon}.
 * AVAILABLE prizes are redeemed with points and use the prize's own short name as the
 * headline; PENDING prizes are free to apply and prefer the nested coupon's headline.
 */
export const buildPrizeFacades = (
  prizes: SingleCheckoutTypes.Prize[],
  status: PrizeStatus,
  { fflRequired, defaultCarts = [] }: { fflRequired?: boolean, defaultCarts?: SingleCheckoutTypes.Coupon['carts'] } = {},
) => (prizes || [])
  .filter((prize) => !fflRequired && prize.status === status)
  .map((prize) => {
    const coupon = prize.coupons?.[0];
    const isAvailableReward = status === PRIZE_STATUS.AVAILABLE;

    return {
      id: prize.id,
      headline: isAvailableReward ? prize.name : (coupon?.headline || prize.name),
      endDate: prize.expirationTime
        ? new Date(prize.expirationTime * 1000).toISOString()
        : undefined,
      minimum: coupon?.minimum || 0,
      couponId: coupon?.id,
      currencyCode: coupon?.carts?.[0]?.currencyCode || 'USD',
      termsConditions: coupon?.termsConditions || null,
      type: coupon?.type || 'REWARDS',
      carts: coupon?.carts || defaultCarts,
      points: isAvailableReward ? prize.points : undefined,
      redeemWithPoints: isAvailableReward || undefined,
      _isPrize: true,
    };
  });

export const createCommunityBrandUrl = (brandPageKey, previewKey, tagId) => {
  const brandUrl = `/brand/${brandPageKey}`;
  const params = new URLSearchParams();

  if (previewKey) {
    params.set('campaignPreviewKey', previewKey);

    if (tagId) {
      params.set('campaignPreviewTagId', tagId);
    }
  }

  return params.toString() ? `${brandUrl}?${params.toString()}` : brandUrl;
};

export const createBrandLessonsUrl = (brandPageKey: string) => `/brand/${brandPageKey}/lessons`;

export const createAvatarUrl = (imageKey, avatarKey) => `/xapi/xds/pub/gimg/${imageKey}/${avatarKey}/avatar.png`;
export const createBrandAvatarUrl = (avatarKey, modifiedTime = undefined) => `/xapi/xds/pub/gimg/${avatarKey}/${
  modifiedTime ? `${new Date(modifiedTime).getTime()}/` : ''}brandAvatar.png`;

export const createProductListingUrl = (orgId, orgKey, previewKey = null, tagId = null) => {
  let url = previewKey ? `/shop/brand/${orgId}/${orgKey}?previewKey=${previewKey}` : `/shop/brand/${orgId}/${orgKey}`;
  if (previewKey && tagId) {
    url += `&tagId=${tagId}`;
  }
  return url;
};

export const createProductListingDealUrl = (orgId, orgKey, deal, previewKey = null, tagId = null) => {
  let url = `/shop/brand/${orgId}/${orgKey}?d=${deal}`;
  if (previewKey) {
    url += `&previewKey=${previewKey}`;
    if (tagId) {
      url += `&tagId=${tagId}`;
    }
  }
  return url;
};

export const createUpdateOrderUrl = (storeId, orderId) =>
  `/checkout/updateOrder/${storeId}/${orderId}`;

export const decParseInt = (string) => parseInt(string, INTEGER_RADIX);

/**
 * Formats an address to an array of string parts
 * @param {CheckoutPropTypes.AddressType} address
 * @param {(part: string, index: number, parts: string[]) => string} [mapper]
 * @returns {string[]}
 */
export const formatAddressToParts = (address, mapper = (part) => part) => (
  address
    ? [[
      address.attention,
      address.address1,
      address.address2,
    ], [
      address.city,
    ], [
      address.state,
      address.postalCode,
    ]]
      .flatMap((part) => part.filter(Boolean).join(' '))
      .filter(Boolean)
      .map(mapper)
    : []
);

/**
 * Calculates whether or not a coupon/offer is expired and returns an indicator boolean,
 * an expiration Date, and a formatted time string calculated from the given now date.
 * @param {{
 *  compact: boolean,
 *  expirationDate: string | Date,
 *  now: number | Date
 * }}
 * @returns {[
 *  expired: boolean,
 *  expirationDate?: Date,
 *  formattedTimeFromNow?: string,
 *  expiresWithin24h: boolean,
 * ]}
 */
export const getCouponExpiration = ({ compact, expirationDate: expirationStr, now }) => {
  if (!expirationStr) {
    return [false, null, null, false];
  }

  const expireDate = new Date(expirationStr);
  const expireDateMS = expireDate.getTime();
  const expiresInDays = (expireDateMS - now) > TIME_DURATIONS.DAY;

  const expireStr =
    expiresInDays
      ? new Intl.DateTimeFormat().format(expireDate)
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      : getFormattedTimeFromNow(expireDate, compact);

  return [expireDateMS <= now, expireDate, expireStr, !expiresInDays];
};

// This recursive version of getDataAttribute can search ancestors
// until it finds one with the attr or reaches the document.
export const getDataAttribute = (e, attr, searchAncestors) => {
  if (e.nodeType !== Node.ELEMENT_NODE) { // is it a dom element?
    return undefined;
  }
  // todo: this can probably just access e?.dataset?.[attr] without lodash
  const found = property(`dataset.${attr}`)(e) || e.getAttribute(`data-${attr}`);
  if (!searchAncestors || found) {
    return property(`dataset.${attr}`)(e) || e.getAttribute(`data-${attr}`);
  }
  return getDataAttribute(e.parentNode, attr, searchAncestors);
};

/**
 * Get the default image for a product.
 */
export const getDefaultProductImage = (
  { product, productOptions }: { product: ProductTypes.Product, productOptions: ProductTypes.ProductOption[] },
) => {
  const defaultImage = product.imageFilenames[0];

  const imageValues = productOptions
    ?.flatMap(({ values }) => values)
    ?.filter(({ imageFilename }) => !!imageFilename) || [];

  const firstInStock = imageValues.find(({ imageFilename, inStock }) => inStock && imageFilename);

  if (!imageValues.length) {
    return defaultImage;
  }

  return firstInStock
    ? firstInStock.imageFilename
    : imageValues[0].imageFilename;
};

export const getEDDAnalyticsData = (estimatedDelivery?: ProductTypes.EstimatedDelivery) => {
  const dayMS = 1000 * 60 * 60 * 24;
  const eddDate = new Date(estimatedDelivery?.estimatedDeliveryDate).getTime();
  const eddDateMax = new Date(estimatedDelivery?.estimatedDeliveryDateMax).getTime();
  const eddDateMin = new Date(estimatedDelivery?.estimatedDeliveryDateMin).getTime();
  const now = Date.now();

  return estimatedDelivery ? {
    eddConfidence: estimatedDelivery.confidence,
    eddDaysFromNow: Math.round((eddDate - now) / dayMS),
    eddDaysFromNowMax: Math.round((eddDateMax - now) / dayMS),
    eddDaysFromNowMin: Math.round((eddDateMin - now) / dayMS),
    eddDaysSpread: Math.round((eddDateMax - eddDateMin) / dayMS),
    eddShippingSpeed: estimatedDelivery.shippingSpeed,
    eddVariableProcessingTime: estimatedDelivery.variableProcessingTime,
  } : {};
};

/**
 * Calculates the remaining time between now and a given end date.
 * @param {Date} endDate
 * @param {boolean} compact
 * @returns {string} `hh:mm:ss` format (or `mm:ss` if compact), zero-padded if necessary.
 */
export const getFormattedTimeFromNow = (endDate, compact = false) => {
  let msUntilEnd = endDate.getTime() - Date.now();

  const hours = Math.floor(msUntilEnd / TIME_DURATIONS.HOUR);
  msUntilEnd -= hours * TIME_DURATIONS.HOUR;
  const minutes = Math.floor(msUntilEnd / TIME_DURATIONS.MINUTE);
  msUntilEnd -= minutes * TIME_DURATIONS.MINUTE;
  const seconds = Math.floor(msUntilEnd / TIME_DURATIONS.SECOND);

  const hoursStr = hours < 10 ? `0${hours}` : hours;
  const minutesStr = minutes < 10 ? `0${minutes}` : minutes;
  const secondsStr = seconds < 10 ? `0${seconds}` : seconds;

  if (compact && !hours) {
    return `${minutesStr}:${secondsStr}`;
  }

  return `${hoursStr}:${minutesStr}:${secondsStr}`;
};

/**
 * Finds an element in an HTML string matching a given selector
 * @param {object} options
 * @param {string} options.html
 * @param {string} options.selector
 * @returns {?HTMLElement}
 */
export const findElementFromHtmlString = ({ html = '', selector }) => {
  const div = document.createElement('div');
  div.innerHTML = html;

  let found;
  try {
    found = div.querySelector(selector);
  } catch { }

  return found;
};

export const isProductDiscountAvailable = (product) => product.discount > 0;

/**
 * Parses an html guidelines string into an object of
 * guidelines parts strings derived from GUIDELINES_PARTS
 * @param {string} guidelinesStr
 * @returns {Record<string, string>}
 *   { [returns]: html, [shipping]: html, ...etc }
 *   only includes found parts
 */
export const parseKnownGuidelinesParts = (guidelinesStr = '') => {
  const div = document.createElement('div');
  div.innerHTML = guidelinesStr;

  const foundParts = Object
    .values(GUIDELINES_PARTS)
    .reduce((acc, partKey) => {
      const foundPart = div.querySelector(`.${partKey}`);

      return foundPart ? { ...acc, [partKey]: foundPart.outerHTML } : acc;
    }, {});

  return foundParts;
};

export const replaceMarkupAssetsUrl = (rawHtml) => {
  const html = rawHtml.replace('{assets.contrib}', context.contribAssetsURL);
  return { __html: html };
};

export const setGuidelinesLocalStorage = (storeId, userId) => {
  const storageName = `${NG_STORAGE_GUIDELINES}_${storeId}_${userId}`;
  localStorage.setItem(storageName, Date.now().toString());
};

export const stripTags = (value) => value.replace(/<{1}[^<>]{1,}>{1}/g, '');

export const uniqueValues = (arr) => arr.filter((v, i, a) => a.indexOf(v) === i);

export const getCurrencyFromCode = (currencyCode) => new Intl.DisplayNames(undefined, { type: 'currency' }).of(currencyCode);

export const addOrUpdateObject = (mapToEdit, id, data) => {
  const updatedMap = mapToEdit || {};
  updatedMap[id] = data;
  return updatedMap;
};
